import moment from 'moment'
import { GUID } from './Services/ServiceTypes'
// import { CommonActions } from '@react-navigation/native'
import { v4 as uuidv4 } from 'uuid'

export interface IWeekAndYear {
  weekYear: number
  week: number
}

interface PromiseFulfilledResult<T> {
  status: 'fulfilled'
  value: T
}

interface PromiseRejectedResult {
  status: 'rejected'
  reason: any
}

type PromiseSettledResult<T> = PromiseFulfilledResult<T> | PromiseRejectedResult

interface HasOrder {
  order: number
}

type GroupedResult<T, G> = {
  groupKey: G
  items: T[]
}

export enum FColor {
  black,
  red,
  green,
  yellow,
  blue,
  cyan,
  magenta,
  white,
  gray,
  redBright,
  greenBright,
  yellowBright,
  blueBright,
  cyanBright,
  magentaBright,
  whiteBright,
}

export default class Util {
  static now: Date | undefined = undefined

  static get Now(): Date {
    return Util.now ?? new Date()
  }

  static set Now(value: Date | undefined) {
    Util.now = value
  }

  static get NowWithoutTime() {
    return Util.dateWithoutTime(Util.Now)
  }

  static toMaxDecimals(value: number, maxDecimals: number) {
    return value.toFixed(maxDecimals).replace(/^0+|0+$/g, '')
  }

  static dateWithoutTime(date: Date) {
    const r = new Date(date.getTime())
    r.setHours(0, 0, 0, 0)
    return r
  }

  static numberOfDaysSpan(d1: Date, d2: Date) {
    const day = 86400000
    return (d2.getTime() - d1.getTime()) / day
  }

  static pad(num: number, size: number) {
    const s = `000000000${num}`
    return s.substr(s.length - size)
  }

  static firstOrDefault(arr: any[], defaultValue: any) {
    if (arr.length > 0) {
      return arr[0]
    }
    return defaultValue
  }

  static firstOrDefault2<T>(arr: T[], defaultValue: T | undefined) {
    if (arr.length > 0) {
      return arr[0]
    }
    return defaultValue
  }

  static formatNow() {
    const d = new Date()
    const h = Util.pad(d.getHours(), 2)
    const m = Util.pad(d.getMinutes(), 2)
    const s = Util.pad(d.getSeconds(), 2)
    const ms = Util.pad(d.getMilliseconds(), 3)
    return h + ':' + m + ':' + s + ':' + ms
  }

  static tryJSONParse(json: string) {
    try {
      return JSON.parse(json)
    } catch {
      return null
    }
  }

  static parseJsonDate(jsonDateString: string) {
    return new Date(parseInt(jsonDateString.replace('/Date(', ''), 10))
  }

  static convertToJsonDate(d: Date) {
    return (
      '/Date(' +
      Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes() + d.getTimezoneOffset(), d.getSeconds(), d.getMilliseconds()) +
      ')/'
    )
  }

  static isWithinSeconds(date: Date, seconds: number): boolean {
    const now = moment()
    const target = moment(date)

    // diff i sekunder (positiv hvis target er i fortiden)
    const diffSeconds = now.diff(target, 'seconds')

    return diffSeconds >= 0 && diffSeconds <= seconds
  }

  static daydiff(first: number, second: number) {
    return Math.round((second - first) / (1000 * 60 * 60 * 24))
  }

  static calcBusinessDays(dDate1: Date, dDate2: Date) {
    // input given as Date objects
    let iDateDiff,
      iAdjust = 0
    if (dDate2 < dDate1) {
      return -1 // error code if dates transposed
    }
    let iWeekday1 = dDate1.getDay() // day of week
    let iWeekday2 = dDate2.getDay()
    iWeekday1 = iWeekday1 === 0 ? 7 : iWeekday1 // change Sunday from 0 to 7
    iWeekday2 = iWeekday2 === 0 ? 7 : iWeekday2
    if (iWeekday1 > 5 && iWeekday2 > 5) {
      iAdjust = 1 // adjustment if both days on weekend
    }
    iWeekday1 = iWeekday1 > 5 ? 5 : iWeekday1 // only count weekdays
    iWeekday2 = iWeekday2 > 5 ? 5 : iWeekday2

    // calculate differnece in weeks (1000mS * 60sec * 60min * 24hrs * 7 days = 604800000)
    const iWeeks = Math.floor((dDate2.getTime() - dDate1.getTime()) / 604800000)

    if (iWeekday1 <= iWeekday2) {
      iDateDiff = iWeeks * 5 + (iWeekday2 - iWeekday1)
    } else {
      iDateDiff = (iWeeks + 1) * 5 - (iWeekday1 - iWeekday2)
    }

    iDateDiff -= iAdjust // take into account both days on weekend

    return iDateDiff + 1 // add 1 because dates are inclusive
  }

  static notEmptyAndCast<TValue>(value: TValue | null | undefined): value is TValue {
    return value !== null && value !== undefined
  }

  static notUndefinedAndCast<TValue>(value: TValue | null | undefined): value is TValue | null {
    return value !== undefined
  }

  static groupBy<K, V>(array: V[], grouper: (item: V) => K) {
    return array.reduce((store, item) => {
      const key = grouper(item)
      if (!store.has(key)) {
        store.set(key, [item])
      } else {
        store.get(key)!.push(item)
      }
      return store
    }, new Map<K, V[]>())
  }

  static groupByExtended<K, V, W>(array: V[], grouper: (item: V) => K, addVToW: (v: V, w: W | undefined) => W) {
    return array.reduce((store, item) => {
      const key = grouper(item)
      const existingW = store.get(key)
      store.set(key, addVToW(item, existingW))
      return store
    }, new Map<K, W>())
  }

  static groupByEx<T, G>(array: T[], mapGroupKey: (item: T) => G): GroupedResult<T, G>[] {
    const groups = new Map<G, T[]>()

    for (const item of array) {
      const key = mapGroupKey(item)
      if (!groups.has(key)) {
        groups.set(key, [])
      }
      groups.get(key)!.push(item)
    }

    return Array.from(groups.entries()).map(([groupKey, items]) => ({
      groupKey,
      items,
    }))
  }

  static distinct<V>(array: V[]): V[] {
    const set = new Set<V>()
    return array.filter(v => {
      if (set.has(v)) return false
      set.add(v)
      return true
    })
  }

  static distinctByKey<V, K>(array: V[], getK: (v: V) => K): V[] {
    const set = new Set<K>()
    return array.filter(v => {
      const k = getK(v)
      if (set.has(k)) return false
      set.add(k)
      return true
    })
  }

  static *seperateArrayWithElement<T, E>(array: T[], element: E, includeLast: boolean) {
    let firstItem = true
    for (const t of array) {
      if (!includeLast && !firstItem) {
        yield element
      }
      yield t
      if (includeLast) {
        yield element
      } else {
        firstItem = false
      }
    }
  }

  static getIso8601WeekAndYear(date: Date): IWeekAndYear {
    // Juster datoen til den nærmeste torsdag (ISO 8601: Uger starter mandag, uge indeholder en torsdag)
    const targetDate = new Date(date)
    const dayOfWeek = (targetDate.getDay() + 6) % 7 // Juster søndag (0) til at være sidste dag (6)
    targetDate.setDate(targetDate.getDate() + 3 - dayOfWeek)

    // Find den første torsdag i ISO-året
    const isoYear = targetDate.getFullYear()
    const firstThursday = new Date(isoYear, 0, 4) // 4. januar er altid i den første ISO-uge
    const firstThursdayDayOfWeek = (firstThursday.getDay() + 6) % 7 // Juster søndag (0)
    firstThursday.setDate(firstThursday.getDate() - firstThursdayDayOfWeek + 3)

    // Beregn antallet af dage mellem targetDate og firstThursday
    const daysDifference = (targetDate.getTime() - firstThursday.getTime()) / (1000 * 60 * 60 * 24)

    // Beregn ugenummeret
    const week = Math.floor(daysDifference / 7) + 1

    // Bestem året og håndter overgangen
    let year = isoYear
    if (week === 53 && targetDate.getMonth() === 0) {
      year-- // Hvis uge 53 i starten af januar
    } else if (week === 1 && targetDate.getMonth() === 11) {
      year++ // Hvis uge 1 i slutningen af december
    }

    // Håndter specielle tilfælde for slutningen af året
    if (date.getMonth() === 11 && week === 1) {
      return { week: 53, weekYear: date.getFullYear() }
    }

    return { week, weekYear: year }
  }

  static getWeekByDate(date: Date): IWeekAndYear {
    return Util.getIso8601WeekAndYear(date)
  }

  static ordinal_suffix_of(i: number) {
    if (i === 0) return null
    const j = i % 10,
      k = i % 100
    if (j === 1 && k !== 11) {
      return i + 'st'
    }
    if (j === 2 && k !== 12) {
      return i + 'nd'
    }
    if (j === 3 && k !== 13) {
      return i + 'rd'
    }
    return i + 'th'
  }

  static special = [
    'zeroth',
    'first',
    'second',
    'third',
    'fourth',
    'fifth',
    'sixth',
    'seventh',
    'eighth',
    'ninth',
    'tenth',
    'eleventh',
    'twelfth',
    'thirteenth',
    'fourteenth',
    'fifteenth',
    'sixteenth',
    'seventeenth',
    'eighteenth',
    'nineteenth',
  ]
  static deca = ['twent', 'thirt', 'fort', 'fift', 'sixt', 'sevent', 'eight', 'ninet', 'tent']

  static stringifyNumber(n: number, pascalCase: boolean = true) {
    let str: string
    if (n > 19) str = `${n}th`
    else if (n < 20) str = Util.special[n]
    else if (n % 10 === 0) str = Util.deca[Math.floor(n / 10) - 2] + 'ieth'
    else str = Util.deca[Math.floor(n / 10) - 2] + 'y-' + Util.special[n % 10]
    return pascalCase ? Util.pascalCaseString(str) : str
  }

  static pascalCaseString = (s: string) => {
    return s.replace(/\w+/g, function (w) {
      return w[0].toUpperCase() + w.slice(1) //.toLowerCase()
    })
  }

  static round(num: number, decimals: number): number {
    //0 = 0
    //1 = 10
    //2 = 100
    const factor = Math.pow(10, decimals)
    console.log(`num * factor = ${num * factor}`, 2)
    return Math.round(num * factor) / factor
  }

  static cloneMap<K, V>(dict: Map<K, V>): Map<K, V> {
    const entries = Array.from(dict.entries())
    return new Map<K, V>(entries)
  }

  static pureMapSet<K, V>(dict: Map<K, V>, key: K, value: V): Map<K, V> {
    //return dict.set(key, value)

    const clonedMap = Util.cloneMap(dict)
    return clonedMap.set(key, value)
  }

  static pureMapDelete<K, V>(dict: Map<K, V>, key: K): Map<K, V> {
    const clonedMap = Util.cloneMap(dict)
    clonedMap.delete(key)
    return clonedMap
  }

  static mapGetSafe = <K, V>(dict: Map<K, V> | undefined, key: K | undefined) => {
    if (dict === undefined || key === undefined) return undefined
    return dict.get(key)
  }

  static cloneSet<K>(dict: Set<K>): Set<K> {
    const keys = Array.from(dict.keys())
    return new Set<K>(keys)
  }

  static pureSetAdd<K>(dict: Set<K>, key: K): Set<K> {
    const clonedSet = Util.cloneSet(dict)
    return clonedSet.add(key)
  }

  static pureSetDelete<K>(dict: Set<K>, key: K): Set<K> {
    const clonedSet = Util.cloneSet(dict)
    clonedSet.delete(key)
    return clonedSet
  }

  static splitArrayIntoArraysOfArrays = <T>(arr: T[], howManyArraysMax: number): T[][] => {
    const result: T[][] = []

    const howManyPerArray = Math.ceil(arr.length / howManyArraysMax)

    let currentArray: T[] = []

    for (const item of arr) {
      currentArray.push(item)

      if (currentArray.length === howManyPerArray) {
        result.push(currentArray)
        currentArray = []
      }
    }

    if (currentArray.length > 0) {
      result.push(currentArray)
    }

    return result
  }

  static isNumber = (str: string) => {
    if (typeof str !== 'string' || str.trim() === '') return false
    return !Number.isNaN(Number(str))
  }

  static strToInt = (str: string): number | undefined => {
    if (Util.isNumber(str)) return parseInt(str, 10)
    return undefined
  }

  static executeActionIfNavigationAllowsIt = (navigation: any, f: () => void) => {
    const state = navigation.getState()
    if (state.routes.length === 0 || state.routes[state.routes.length - 1].name !== 'Error') {
      f()
    }
  }

  static getEnumKeys = (E: any) => Object.keys(E).filter(k => typeof E[k as any] === 'number')
  static getEnumValues = <T>(E: any) => Util.getEnumKeys(E).map(k => (E as any)[k as any]) as T[] // [0, 1]

  static splitListToXLists = <T>(arr: T[], maxNumberOfLists: number, mapToIndex: (t: T) => number) => {
    const dictList = new Array(maxNumberOfLists).fill(null).map(_ => [] as T[])

    for (const t of arr) {
      const idx = mapToIndex(t)
      if (idx < 0 || idx > maxNumberOfLists - 1) throw new Error('Invalid destination index')
      dictList[idx].push(t)
    }

    return dictList
  }

  static splitListTo2ListsByIndex = <T>(arr: T[], isInListIndex: (t: T) => number): [T[], T[]] => {
    const list1: T[] = []
    const list2: T[] = []

    for (const t of arr) {
      const idx = isInListIndex(t)
      if (idx === 0) {
        list1.push(t)
      } else if (idx === 1) {
        list2.push(t)
      } else {
        throw new Error('Invalid list index: must be 0 or 1')
      }
    }

    return [list1, list2]
  }

  static splitListTo3ListsByIndex = <T>(arr: T[], isInListIndex: (t: T) => number): [T[], T[], T[]] => {
    const list1: T[] = []
    const list2: T[] = []
    const list3: T[] = []

    for (const t of arr) {
      const idx = isInListIndex(t)
      if (idx === 0) {
        list1.push(t)
      } else if (idx === 1) {
        list2.push(t)
      } else if (idx === 2) {
        list3.push(t)
      } else {
        throw new Error('Invalid list index: must be 0, 1, or 2')
      }
    }

    return [list1, list2, list3]
  }

  static deserializeMap<K, V>(json: string, mapK: (x: any) => K, mapV: (x: any) => V) {
    const m = new Map<K, V>()
    const obj = JSON.parse(json)
    for (var value in obj) {
      m.set(mapK(value), mapV(obj[value]))
    }
    return m
  }

  static splitArrayByPosition<T>(array: T[], position: number) {
    return [array.slice(0, position), array.slice(position)]
  }

  static promisesAllSettledSequentially = async <T>(promises: (() => Promise<T>)[]): Promise<PromiseSettledResult<T>[]> => {
    const result = [] as PromiseSettledResult<T>[]
    for (const p of promises) {
      try {
        const value = await p()
        result.push({
          status: 'fulfilled',
          value,
        } as PromiseFulfilledResult<T>)
      } catch (e) {
        result.push({
          status: 'rejected',
          reason: e,
        } as PromiseRejectedResult)
      }
    }
    return result
  }

  static promisesAllSequentially = async <T>(promises: (() => Promise<T>)[]): Promise<T[]> => {
    const result = [] as T[]
    for (const p of promises) {
      result.push(await p())
    }
    return result
  }

  static objToDebugString = (obj: any) => {
    if (obj === undefined) return 'undefined'
    if (obj === null) return 'null'
    return JSON.stringify(obj)
  }

  static sleep = (ms: number): Promise<void> => {
    return new Promise<void>(resolve => {
      setTimeout(resolve, ms)
    })
  }

  static createArray<T>(amount: number, mapT: (idx: number) => T): T[] {
    return new Array(amount).fill(null).map((_, i) => mapT(i))
  }

  static base64ToSourceUri = (base64: string | null) => (base64 ? `data:image/png;base64,${base64}` : null)

  static insertAt = <T>(array: T[], index: number, item: T): T[] => {
    return [...array.slice(0, index), item, ...array.slice(index)]
  }

  static removeAt = <T>(array: T[], index: number): T[] => {
    return [...array.slice(0, index), ...array.slice(index + 1)]
  }

  static isNullOrWhitespace = (input: string | null | undefined): boolean => {
    return !input || input.trim() === ''
  }

  static orderByNumber = <T>(items: T[], orderBy: (item: T) => number) => [...items].sort((a, b) => orderBy(a) - orderBy(b))

  static orderByAlpha = <T>(items: T[], orderBy: (item: T) => string) =>
    [...items].sort((a, b) => (orderBy(a).toLocaleLowerCase() > orderBy(b).toLocaleLowerCase() ? 1 : -1))

  static getMaxItems = <T>(array: T[], max: number): T[] => array.slice(0, max)

  static serializeError = (error: unknown) => {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      }
    }
    return { message: String(error) }
  }

  static getMaxOrDefault = (numbers: number[], defaultValue: number): number => {
    if (numbers.length === 0) {
      return defaultValue
    }
    return Math.max(...numbers)
  }

  static isValidEmail = (email: string): boolean => {
    // Simpelt men robust regex
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return regex.test(email)
  }

  static get UUID() {
    return uuidv4()
  }

  static formatBytesSI = (bytes: number, decimals = 1): string => {
    if (!Number.isFinite(bytes)) return '0 B'
    if (bytes === 0) return '0 B'

    const k = 1000
    const dm = Math.max(decimals, 0)
    const sizes = ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB']

    const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k))
    const value = bytes / Math.pow(k, i)

    return `${parseFloat(value.toFixed(dm))} ${sizes[i]}`
  }

  static htmlEncode = (s: string) => {
    const d = document.createElement('div')
    d.innerText = s
    return d.innerHTML
  }
}
